import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Shell } from "@/components/Shell";
import {
  IconPhone as Phone, IconMail as Mail, IconPin as MapPin, IconClockFast as Clock,
  IconCar as Car, IconFlag as Flag, IconUser as User, IconSend as Send, IconTimes as XCircle,
  IconNote as StickyNote, IconRefresh as RefreshCw, IconBuilding as Building2, IconAlert as AlertTriangle,
  IconCalendar as CalendarRange, IconPhoneOff as PhoneOff, IconCheckCircle as CheckCircle2,
  IconHand as HandMetal, IconArrowRightCircle as ArrowRightCircle, IconClipboard as ClipboardCheck, IconBan as Ban,
} from "@/components/BrandGraphics";

const CRM_LABELS = {
  nouveau: "Nouveau", en_cours: "En cours", qualifie: "Qualifié", route: "Routé (Cas B)",
  transmis_planpermis: "Transmis Planpermis (Cas C)", inscrit: "Inscrit (Cas A)",
  refuse: "Refusé", injoignable: "Injoignable", capacite_insuffisante: "Capacité insuffisante",
};
const CRM_CLASSES = {
  nouveau: "bg-neutral-100 text-neutral-700 border-neutral-200",
  en_cours: "bg-sky-50 text-sky-700 border-sky-200",
  qualifie: "bg-indigo-50 text-indigo-700 border-indigo-200",
  route: "bg-violet-50 text-violet-700 border-violet-200",
  transmis_planpermis: "bg-amber-50 text-amber-700 border-amber-200",
  inscrit: "bg-emerald-50 text-emerald-700 border-emerald-200",
  refuse: "bg-red-50 text-red-700 border-red-200",
  injoignable: "bg-neutral-100 text-neutral-600 border-neutral-300",
  capacite_insuffisante: "bg-orange-50 text-orange-700 border-orange-200",
};
const THEMATICS = [
  ["permis_accelere", "Permis accéléré"],
  ["transfert", "Transfert d'auto-école"],
  ["date_rapide", "Date d'examen rapide"],
  ["autre", "Autre"],
];

const CrmBadge = ({ status }) => (
  <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-bold ${CRM_CLASSES[status] || CRM_CLASSES.nouveau}`}>
    {CRM_LABELS[status] || status}
  </span>
);

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};
const frDay = (d) => (d ? String(d).slice(0, 10).split("-").reverse().join("/") : "");
const examWindow = (l) => {
  const s = frDay(l?.exam_date_start); const e = frDay(l?.exam_date_end);
  if (s && e) return `${s} → ${e}`; if (s) return `dès le ${s}`; if (e) return `avant le ${e}`; return "";
};

const InfoRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-2.5">
    <Icon className="mt-0.5 h-4 w-4 shrink-0 text-neutral-400" />
    <div>
      <p className="text-[11px] uppercase tracking-wide text-neutral-400">{label}</p>
      <p className="text-sm font-medium text-neutral-800">{value || "—"}</p>
    </div>
  </div>
);

export default function QualifDashboard() {
  const [scope, setScope] = useState("inbox");
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ to_qualify: 0, oriented: 0, rejected: 0, sla_breaches: 0 });
  const [selected, setSelected] = useState(null);
  const [partners, setPartners] = useState([]);
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  // workflow states
  const [qual, setQual] = useState({ thematic: "", summary: "" });
  const [caseASchool, setCaseASchool] = useState("");
  const [insc, setInsc] = useState({ inscription_date: "", contract_amount: "", contract_ref: "", proof_note: "" });
  const [sugg, setSugg] = useState(null);

  const loadList = useCallback(async (keepId) => {
    setLoading(true);
    try {
      const [{ data: list }, { data: s }] = await Promise.all([
        api.get(`/qualif/leads?scope=${scope}`),
        api.get("/qualif/stats"),
      ]);
      setLeads(list); setStats(s);
      if (keepId) { const f = list.find((l) => l.id === keepId); if (f) openLead(f); }
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Erreur de chargement");
    } finally { setLoading(false); }
  }, [scope]);

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { api.get("/qualif/partners").then(({ data }) => setPartners(data)).catch(() => {}); }, []);

  const openLead = async (lead) => {
    setNote(""); setSugg(null); setCaseASchool("");
    setQual({ thematic: lead.qualification?.thematic || "", summary: "" });
    setInsc({ inscription_date: "", contract_amount: "", contract_ref: "", proof_note: "" });
    try { const { data } = await api.get(`/qualif/leads/${lead.id}`); setSelected(data); }
    catch { setSelected(lead); }
  };
  const refreshSelected = async () => {
    if (!selected) return;
    const { data } = await api.get(`/qualif/leads/${selected.id}`);
    setSelected(data);
  };

  const act = async (fn, okMsg, { close = false, refresh = true } = {}) => {
    setBusy(true);
    try {
      await fn();
      if (okMsg) toast.success(okMsg);
      if (close) { setSelected(null); await loadList(); }
      else { await loadList(selected?.id); if (refresh) await refreshSelected(); }
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Erreur");
    } finally { setBusy(false); }
  };

  const cs = selected?.crm_status || "nouveau";

  const addNote = () => note.trim() && act(() => api.post(`/qualif/leads/${selected.id}/note`, { text: note.trim() }).then(() => setNote("")), "Note ajoutée");
  const take = () => act(() => api.post(`/qualif/leads/${selected.id}/take`), "Prospect pris en charge");
  const attempt = () => act(() => api.post(`/qualif/leads/${selected.id}/attempt`), "Tentative enregistrée");
  const qualify = () => {
    if (!qual.thematic) { toast.error("Choisis la thématique."); return; }
    act(() => api.post(`/qualif/leads/${selected.id}/qualify`, { thematic: qual.thematic, summary: qual.summary }), "Prospect qualifié");
  };
  const setStatus = (crm_status, ask) => {
    let reason = "";
    if (ask) { reason = window.prompt(ask) || ""; if (!reason.trim()) return; }
    act(() => api.post(`/qualif/leads/${selected.id}/status`, { crm_status, reason }), "Statut mis à jour", { close: true });
  };
  const routeCase = (c, school_id) => act(
    () => api.post(`/qualif/leads/${selected.id}/route/${c}`, { school_id: school_id || null }),
    c === "C" ? "Transmis à Planpermis" : `Orienté (Cas ${c})`,
    { close: c !== "A" });
  const loadSuggestions = () => act(async () => {
    const { data } = await api.get(`/qualif/leads/${selected.id}/school-suggestions`);
    setSugg(data);
  }, null, { refresh: false });
  const inscrire = () => {
    if (!insc.contract_amount) { toast.error("Indique le montant du contrat."); return; }
    act(() => api.post(`/qualif/leads/${selected.id}/inscrire`, {
      inscription_date: insc.inscription_date, contract_amount: Number(insc.contract_amount),
      contract_ref: insc.contract_ref, proof_note: insc.proof_note,
    }), "Inscription déclarée ✅", { close: true });
  };

  return (
    <Shell role="qualificateur">
      <div className="px-5 py-6 sm:px-8 lg:px-10">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-extrabold tracking-tight text-neutral-900" data-testid="qualif-title">Suivi des prospects</h1>
            <p className="mt-1 text-sm text-neutral-500">Prends en charge, appelle sous 72h, qualifie puis décide du routage (Cas A/B/C).</p>
          </div>
          <button onClick={() => loadList(selected?.id)} data-testid="qualif-refresh"
            className="inline-flex items-center gap-2 rounded-full border border-neutral-300 bg-white px-4 py-2 text-sm font-semibold text-neutral-700 hover:bg-neutral-50">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Actualiser
          </button>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4 sm:max-w-2xl">
          <div className="rounded-xl border border-sky-200 bg-sky-50 p-3"><p className="text-2xl font-extrabold text-sky-700">{stats.to_qualify}</p><p className="text-xs font-medium text-sky-600">À traiter</p></div>
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3"><p className="text-2xl font-extrabold text-emerald-700">{stats.oriented}</p><p className="text-xs font-medium text-emerald-600">Orientés/Inscrits</p></div>
          <div className="rounded-xl border border-red-200 bg-red-50 p-3"><p className="text-2xl font-extrabold text-red-700">{stats.rejected}</p><p className="text-xs font-medium text-red-600">Refusés</p></div>
          <div className="rounded-xl border border-orange-200 bg-orange-50 p-3" data-testid="qualif-stat-sla"><p className="text-2xl font-extrabold text-orange-700">{stats.sla_breaches || 0}</p><p className="text-xs font-medium text-orange-600">SLA dépassé</p></div>
        </div>

        <div className="mt-6 flex gap-2">
          {[{ key: "inbox", label: "À traiter" }, { key: "processed", label: "Traités" }, { key: "all", label: "Tous" }].map((t) => (
            <button key={t.key} onClick={() => { setScope(t.key); setSelected(null); }} data-testid={`qualif-tab-${t.key}`}
              className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${scope === t.key ? "bg-brand text-white" : "bg-neutral-100 text-neutral-600 hover:bg-neutral-200"}`}>{t.label}</button>
          ))}
        </div>

        <div className="mt-5 grid gap-5 lg:grid-cols-12">
          {/* List */}
          <div className="lg:col-span-5">
            <div className="space-y-2" data-testid="qualif-list">
              {leads.length === 0 && !loading && (
                <div className="rounded-xl border border-dashed border-neutral-300 bg-white p-8 text-center text-sm text-neutral-500">Aucun prospect ici.</div>
              )}
              {leads.map((l) => (
                <button key={l.id} onClick={() => openLead(l)} data-testid={`qualif-item-${l.id}`}
                  className={`w-full rounded-xl border bg-white p-4 text-left transition-colors hover:border-brand ${selected?.id === l.id ? "border-brand ring-1 ring-brand" : "border-neutral-200"}`}>
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-bold text-neutral-900">
                      {l.first_name} {l.last_name}
                      {l.sla_overdue && <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-bold text-orange-700"><Clock className="h-3 w-3" /> SLA</span>}
                      {l.urgent && <span className="ml-1 inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-bold text-red-700"><AlertTriangle className="h-3 w-3" /> Urgent</span>}
                    </p>
                    <CrmBadge status={l.crm_status || "nouveau"} />
                  </div>
                  <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-neutral-500">
                    <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{l.city} ({l.postal_code})</span>
                    <span>{fmtDate(l.created_at)}</span>
                  </div>
                  {l.assigned_school_name && <p className="mt-1 text-xs font-medium text-violet-600">→ {l.assigned_school_name}</p>}
                </button>
              ))}
            </div>
          </div>

          {/* Detail */}
          <div className="lg:col-span-7">
            {!selected ? (
              <div className="grid h-full min-h-[300px] place-items-center rounded-xl border border-dashed border-neutral-300 bg-white p-8 text-center text-sm text-neutral-400">Sélectionne un prospect.</div>
            ) : (
              <div className="rounded-xl border border-neutral-200 bg-white p-5" data-testid="qualif-detail">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h2 className="text-xl font-extrabold text-neutral-900">{selected.first_name} {selected.last_name}</h2>
                    <div className="mt-1"><CrmBadge status={cs} /></div>
                  </div>
                  <a href={`tel:${selected.phone}`} className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-700"><Phone className="h-4 w-4" /> Appeler</a>
                </div>

                <div className="mt-5 grid gap-4 sm:grid-cols-2">
                  <InfoRow icon={Phone} label="Téléphone" value={selected.phone} />
                  <InfoRow icon={Mail} label="Email" value={selected.email} />
                  <InfoRow icon={MapPin} label="Ville" value={`${selected.city || ""} ${selected.postal_code ? `(${selected.postal_code})` : ""}`} />
                  <InfoRow icon={User} label="Situation" value={selected.situation} />
                  <InfoRow icon={Flag} label="Besoin" value={selected.need_type} />
                  <InfoRow icon={Clock} label="Délai" value={selected.urgency} />
                  <InfoRow icon={CalendarRange} label="Dates examen" value={examWindow(selected)} />
                  <InfoRow icon={Building2} label="Ancienne auto-école" value={selected.previous_school} />
                </div>

                {/* ===================== WORKFLOW ===================== */}
                <div className="mt-6 rounded-xl border border-neutral-200 bg-neutral-50 p-4" data-testid="qualif-workflow">
                  {cs === "nouveau" && (
                    <div>
                      <p className="text-sm font-bold text-neutral-800">Étape 1 — Prise en charge</p>
                      <p className="mt-0.5 text-xs text-neutral-500">Clique pour prendre le lead en charge (SLA 72h). Il passera « En cours ».</p>
                      <button onClick={take} disabled={busy} data-testid="qualif-take-btn"
                        className="mt-3 inline-flex items-center gap-2 rounded-lg bg-brand px-5 py-2.5 text-sm font-bold text-white hover:opacity-90 disabled:opacity-50">
                        <HandMetal className="h-4 w-4" /> Prendre en charge
                      </button>
                    </div>
                  )}

                  {cs === "en_cours" && (
                    <div>
                      <p className="text-sm font-bold text-neutral-800">Étape 2 — Appel & qualification</p>
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <button onClick={attempt} disabled={busy} data-testid="qualif-attempt-btn"
                          className="inline-flex items-center gap-2 rounded-lg border border-neutral-300 bg-white px-3 py-2 text-xs font-semibold text-neutral-700 hover:border-neutral-500">
                          <PhoneOff className="h-4 w-4" /> Sans réponse ({selected.call_attempts || 0}/3)
                        </button>
                      </div>
                      <div className="mt-3 grid gap-2 sm:grid-cols-2">
                        <select value={qual.thematic} onChange={(e) => setQual((q) => ({ ...q, thematic: e.target.value }))} data-testid="qualif-thematic"
                          className="rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm outline-none focus:border-brand">
                          <option value="">— Thématique —</option>
                          {THEMATICS.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                        </select>
                        <input value={qual.summary} onChange={(e) => setQual((q) => ({ ...q, summary: e.target.value }))} placeholder="Synthèse de l'appel"
                          className="rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm outline-none focus:border-brand" />
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <button onClick={qualify} disabled={busy} data-testid="qualif-qualify-btn"
                          className="inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2 text-sm font-bold text-white hover:opacity-90 disabled:opacity-50">
                          <CheckCircle2 className="h-4 w-4" /> Marquer qualifié
                        </button>
                        <button onClick={() => setStatus("refuse", "Raison du refus ?")} disabled={busy} data-testid="qualif-refuse-btn"
                          className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-white px-3 py-2 text-sm font-semibold text-red-600 hover:bg-red-50"><XCircle className="h-4 w-4" /> Refusé</button>
                        <button onClick={() => setStatus("capacite_insuffisante", "Précision ?")} disabled={busy}
                          className="inline-flex items-center gap-2 rounded-lg border border-orange-200 bg-white px-3 py-2 text-sm font-semibold text-orange-600 hover:bg-orange-50"><Ban className="h-4 w-4" /> Capacité insuffisante</button>
                      </div>
                    </div>
                  )}

                  {cs === "qualifie" && selected.route_case !== "A" && (
                    <div>
                      <p className="text-sm font-bold text-neutral-800">Étape 3 — Décision de routage</p>
                      {/* Cas A */}
                      <div className="mt-3 rounded-lg border border-neutral-200 bg-white p-3">
                        <p className="text-xs font-bold text-emerald-700">Cas A — Le partenaire inscrit (dans sa zone)</p>
                        <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                          <select value={caseASchool} onChange={(e) => setCaseASchool(e.target.value)} data-testid="qualif-casea-school"
                            className="flex-1 rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm outline-none focus:border-brand">
                            <option value="">— Mon auto-école —</option>
                            {partners.map((p) => <option key={p.id} value={p.id}>{p.name} {p.city ? `— ${p.city}` : ""}</option>)}
                          </select>
                          <button onClick={() => caseASchool ? routeCase("A", caseASchool) : toast.error("Choisis ton auto-école.")} disabled={busy} data-testid="qualif-casea-btn"
                            className="inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-700 disabled:opacity-50"><ArrowRightCircle className="h-4 w-4" /> Cas A</button>
                        </div>
                      </div>
                      {/* Cas B */}
                      <div className="mt-3 rounded-lg border border-neutral-200 bg-white p-3">
                        <p className="text-xs font-bold text-violet-700">Cas B — Auto-école du réseau (hors zone)</p>
                        <button onClick={loadSuggestions} disabled={busy} data-testid="qualif-casb-load"
                          className="mt-2 inline-flex items-center gap-2 rounded-lg border border-violet-200 bg-violet-50 px-3 py-2 text-xs font-semibold text-violet-700 hover:bg-violet-100">Voir les suggestions</button>
                        {sugg && (
                          <div className="mt-2 space-y-1.5" data-testid="qualif-casb-suggestions">
                            {sugg.suggestions.length === 0 && <p className="text-xs text-neutral-400">Aucune auto-école du réseau. Passe en Cas C.</p>}
                            {sugg.suggestions.map((sc) => (
                              <div key={sc.id} className="flex items-center justify-between gap-2 rounded-md border border-neutral-200 px-3 py-2">
                                <div className="min-w-0">
                                  <p className="truncate text-sm font-semibold text-neutral-900">{sc.name} {sc.eligible && <span className="ml-1 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700">éligible</span>}</p>
                                  <p className="text-[11px] text-neutral-500">{sc.city} · conv {sc.conversion}% · {sc.rating}/5 · score {sc.score}</p>
                                </div>
                                <button onClick={() => routeCase("B", sc.id)} disabled={busy}
                                  className="shrink-0 rounded-md bg-violet-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-violet-700 disabled:opacity-50">Router</button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      {/* Cas C */}
                      <div className="mt-3 rounded-lg border border-neutral-200 bg-white p-3">
                        <p className="text-xs font-bold text-amber-700">Cas C — Aucune auto-école dispo</p>
                        <button onClick={() => routeCase("C")} disabled={busy} data-testid="qualif-casc-btn"
                          className="mt-2 inline-flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-700 hover:bg-amber-100"><Send className="h-4 w-4" /> Transmettre à Planpermis</button>
                      </div>
                    </div>
                  )}

                  {cs === "qualifie" && selected.route_case === "A" && (
                    <div data-testid="qualif-inscription">
                      <p className="text-sm font-bold text-neutral-800">Étape 5 — Déclaration d&apos;inscription (Cas A)</p>
                      <p className="mt-0.5 text-xs text-neutral-500">Dès signature + 1er paiement. Délai max 7 jours.</p>
                      <div className="mt-3 grid gap-2 sm:grid-cols-2">
                        <input type="date" value={insc.inscription_date} onChange={(e) => setInsc((s) => ({ ...s, inscription_date: e.target.value }))} className="rounded-lg border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-brand" />
                        <input value={insc.contract_amount} onChange={(e) => setInsc((s) => ({ ...s, contract_amount: e.target.value.replace(/[^\d.]/g, "") }))} placeholder="Montant du contrat (€)" inputMode="decimal" data-testid="insc-amount" className="rounded-lg border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-brand" />
                        <input value={insc.contract_ref} onChange={(e) => setInsc((s) => ({ ...s, contract_ref: e.target.value }))} placeholder="Référence contrat" className="rounded-lg border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-brand" />
                        <input value={insc.proof_note} onChange={(e) => setInsc((s) => ({ ...s, proof_note: e.target.value }))} placeholder="Justificatif (note / lien)" className="rounded-lg border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-brand" />
                      </div>
                      <button onClick={inscrire} disabled={busy} data-testid="qualif-inscrire-btn"
                        className="mt-3 inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-emerald-700 disabled:opacity-50"><ClipboardCheck className="h-4 w-4" /> Déclarer l&apos;inscription</button>
                    </div>
                  )}

                  {["route", "inscrit", "transmis_planpermis", "refuse", "injoignable", "capacite_insuffisante"].includes(cs) && (
                    <div>
                      <p className="text-sm font-bold text-neutral-800">Dossier traité — {CRM_LABELS[cs]}</p>
                      {selected.assigned_school_name && <p className="mt-1 text-xs text-neutral-600">Auto-école : {selected.assigned_school_name}</p>}
                      {selected.inscription && <p className="mt-1 text-xs text-neutral-600">Inscription : {Number(selected.inscription.contract_amount || 0).toLocaleString("fr-FR")} € · réf {selected.inscription.contract_ref || "—"}</p>}
                    </div>
                  )}
                </div>

                {/* Notes */}
                <div className="mt-6">
                  <p className="flex items-center gap-2 text-sm font-bold text-neutral-800"><StickyNote className="h-4 w-4 text-brand" /> Notes</p>
                  <div className="mt-2 space-y-2">
                    {(selected.notes || []).map((n) => (
                      <div key={n.id} className="rounded-lg border border-neutral-100 bg-neutral-50 p-2.5 text-sm">
                        <p className="text-neutral-800">{n.text}</p>
                        <p className="mt-0.5 text-[11px] text-neutral-400">{n.author} · {fmtDate(n.created_at)}</p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-2 flex gap-2">
                    <input value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addNote()} data-testid="qualif-note-input"
                      placeholder="Ajouter une note…" className="flex-1 rounded-lg border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-brand" />
                    <button onClick={addNote} disabled={busy || !note.trim()} data-testid="qualif-note-add" className="rounded-lg bg-neutral-900 px-4 py-2 text-sm font-semibold text-white hover:bg-brand disabled:opacity-50">Ajouter</button>
                  </div>
                </div>

                {/* Timeline */}
                {(selected.timeline || []).length > 0 && (
                  <div className="mt-6">
                    <p className="text-sm font-bold text-neutral-800">Historique</p>
                    <ol className="mt-2 space-y-1.5 border-l border-neutral-200 pl-4">
                      {(selected.timeline || []).slice().reverse().map((t, i) => (
                        <li key={i} className="relative text-xs text-neutral-600">
                          <span className="absolute -left-[21px] top-1 h-2 w-2 rounded-full bg-brand" />
                          <span className="text-neutral-400">{fmtDate(t.at)} · </span>{t.event}
                        </li>
                      ))}
                    </ol>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </Shell>
  );
}
